import React from 'react';
import { CloseOutlined } from '@ant-design/icons';

import { LandingChevronRight } from 'assets/svg/landing/landingIcons';
import StudioCardIcon from 'components/atoms/Landing/StudioCardIcon';
import { STUDIO_CARD_SHELL } from 'components/molecules/Landing/StudioCard';
import {
    RGM_HOME_STUDIO_PANEL,
    RGM_HOME_STUDIO_PANEL_CLOSE,
    rgmStudioColumnTestId,
    rgmStudioRouteTestId,
} from 'constants/landingTestIds';
import { NavScreenOption } from 'hooks/nav/useAccessibleNavScreens';
import { StudioGroup } from 'types/landing';

interface Props {
    group: StudioGroup;
    onClose: () => void;
    onSelect: (group: StudioGroup, item: NavScreenOption) => void;
}

/**
 * The expanded state of a Studio card: header, divider, then one labelled block of route pills
 * per option column. Rendered as a full-width sibling immediately after the row containing the
 * open card, which is what makes both Figma expansion states fall out of one code path.
 */
function StudioDetailPanel({ group, onClose, onSelect }: Props) {
    return (
        <div
            data-testid={RGM_HOME_STUDIO_PANEL}
            className={`${STUDIO_CARD_SHELL} flex w-full flex-col gap-5 border-filterV2Border p-5 shadow-landingCardHover`}
        >
            <div className="relative flex w-full items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                    <StudioCardIcon icon={group.icon} groupKey={group.key} />
                    <div className="flex flex-col justify-center gap-1 leading-[1.2]">
                        <p className="font-sm font-medium text-blackTitle">{group.label}</p>
                        <p className="font-xxs font-normal text-filterV2IconMuted">{group.description}</p>
                    </div>
                </div>
                <button
                    type="button"
                    aria-label="Close"
                    data-testid={RGM_HOME_STUDIO_PANEL_CLOSE}
                    onClick={onClose}
                    className="flex size-[22px] shrink-0 items-center justify-center rounded text-navV2TextMuted hover:bg-filterV2PrimaryBg"
                >
                    <CloseOutlined className="text-[16px]" />
                </button>
            </div>

            <div className="w-full border-t border-filterV2Border" />

            <div className="relative flex w-full flex-col gap-4">
                {/* A sub-type with no routes under it (e.g. "Predictive" when nothing in it is
                    accessible) is dropped entirely rather than shown as an empty labelled block. */}
                {group.columns
                    .filter((column) => column.items.length)
                    .map((column) => (
                    <div key={column.label} className="flex w-full flex-col gap-2">
                        <p
                            data-testid={rgmStudioColumnTestId(group.key, column.label)}
                            className="font-xxs font-medium leading-[1.2] text-filterV2TextSecondary"
                        >
                            {column.label}
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                            {column.items.map((item) => (
                                <button
                                    type="button"
                                    key={`${column.label}-${item.path}`}
                                    data-testid={rgmStudioRouteTestId(group.key, item.id || item.path)}
                                    onClick={() => onSelect(group, item)}
                                    className="flex items-center gap-2 rounded-md border border-filterV2Border bg-white px-2 py-1.5 font-xxs font-normal leading-[1.2] text-filterV2TextPrimary hover:bg-filterV2PrimaryBg"
                                >
                                    {item.label}
                                    <LandingChevronRight className="size-4 shrink-0" />
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default StudioDetailPanel;
